import { z } from 'zod';
import {
  CARD_TITLE_MAX_LENGTH,
  SHORT_LINK_MAX_LENGTH,
  cardNumberSchema,
  cardReferenceSchema,
} from './board';
import { cardLocationPutSchema } from './card-location';
import { ulidSchema } from './id';
import { RANK_MAX_LENGTH } from './rank';

/**
 * Cards on the board face — the contract layer for block B12.
 *
 * `BE-12.1` (`FR-BOARD-020` add a card to a list, positioned by lexorank),
 * `BE-12.2` (`FR-BOARD-022` rename a card without opening it), `BE-12.3`
 * (`FR-BOARD-024` persist and read card order, and converge when two members
 * reorder the same list at once) and `BE-12.4` (`FR-BOARD-025` drag a card
 * within a list and between lists on the same board).
 *
 * **This is the second product-content file in this package, and the first one
 * whose subject a user actually types work into.** `./list` was the first —
 * everything before it (`./user`, `./workspace`, `./board`) is identity or
 * permission — and a list is a container. A card is the thing the container
 * holds, which is why four requirements in this block are about *where* it is
 * rather than *what it says*.
 *
 * ---------------------------------------------------------------------------
 * THIS IS NOT `BE-02.1`, AND IT IS NARROWER THAN `BE-02.1`'S CARD WOULD BE
 * ---------------------------------------------------------------------------
 * `BE-02.1` — the full Phase 1 object model (board, list, card, comment, label,
 * notification) — is a different item and stays PENDING. `./list` recorded
 * exactly this and the same sentence applies here with more force, because
 * "card" is the noun `BE-02.1` is mostly *about*: what is below is what B12's
 * four routes accept and return, added with the routes that use it. A field
 * written "while we are in the file" against no route is how `BE-02.1` quietly
 * becomes unreviewable — and on a table with as many columns as `cards`, that
 * failure is one edit away at all times.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS DELIBERATELY ABSENT, so that its absence reads as a decision.
 * Every one of these is a real column on `cards` with no B12 route behind it.
 * ---------------------------------------------------------------------------
 *   - **`description`.** `cards.description text` — `FR-CARD-003`, owned by
 *     `BE-14.2` (the card back's field-scoped description patch). B12 renames a
 *     card from the board face and never opens it; `FR-BOARD-022`'s quick edit
 *     is a *title* edit with actions beside it, and the actions are other
 *     blocks'.
 *   - **`completed` and the dates.** `cards.completed`, `cards.start_at`,
 *     `cards.due_at`, `cards.due_reminder_minutes_before` — `FR-CARD-004`,
 *     `FR-CARD-013`, `FR-CARD-014`, owned by `BE-24.x`. (`README.md` §C.2
 *     records — and, since B24's requirement-doc pass, records as **settled**
 *     — that the due-date reminder model was a conflict between a per-card
 *     offset and a per-board setting; `FR-CARD-014`'s per-card model won and
 *     `FR-NOTIF-003` was amended rather than deleted. A field here would still
 *     be premature: it is `BE-24.x`'s to add with the routes that use it, on
 *     this file's own stated rule.)
 *   - **Labels and members.** `card_labels` and `card_members` are join tables,
 *     not columns, and they are `BE-15.x` (`FR-CARD-009`, `FR-CARD-010`) and
 *     `BE-14.3` (`FR-CARD-007`, `FR-CARD-008`). They are the **whole** visual
 *     vocabulary of a Phase 1 card front (B13's note), which is exactly why
 *     they must arrive with the block that renders them rather than as two
 *     empty arrays this block would never populate.
 *   - **The cover.** `cards.cover_*` — `FR-CARD-021`, Phase 2, `BE-25.x`.
 *   - **Location.** `02-card-detail.md` never defines the field Map view is
 *     built on — see `CLAUDE.md`, "Forwards into a document are not reliable".
 *     Nothing here should look like the beginning of it.
 *   - **`inbox_owner_id`.** `ck_cards_parent` makes a card *either* board-and-
 *     list-parented *or* inbox-parented, never both and never neither. Every
 *     card B12 creates takes the first arm, so this file's `boardId` and
 *     `listId` are non-nullable and the inbox card is **inexpressible** here.
 *     That is `B74`'s (Inbox capture) and the block that builds it decides
 *     whether it needs a view of its own — the same call `cardAddressSchema`
 *     already made in `./board` for the same reason.
 *   - **`archivedAt` and `deletedAt` as timestamps.** `archived` below is one
 *     boolean, on `listSchema.archived`'s and `boardSchema.closed`'s exact
 *     footing; `deletedAt` could only ever be `null`, because every read path
 *     carries `deleted_at IS NULL` and no route writes it back. See the
 *     lifecycle note on `cardSchema`.
 *   - **Comments and the activity feed.** `FR-COLLAB-*`, B16 and B28. A card
 *     view carrying a comment count is a field with no route behind it today
 *     and a query with a cost tomorrow.
 *
 * The file follows the division `./user`, `./workspace`, `./board` and `./list`
 * follow, for the same reason: **input primitives**, which validate and
 * normalise what a route accepts, and **views**, which describe what a route
 * returns. An input primitive reused as a response field is the mistake the
 * division prevents — a response schema that trims returns a title the database
 * does not hold, and one that rejects refuses to serve a row that legitimately
 * exists.
 */

// ---------------------------------------------------------------------------
// Column widths.
//
// ⚠ THERE IS NO `CARD_TITLE_MAX_LENGTH` DECLARED IN THIS FILE, AND THAT IS THE
// DELIBERATE OPPOSITE OF WHAT `./list` DID. Read both notes together before
// "harmonising" either.
//
// `./board` already declares `CARD_TITLE_MAX_LENGTH = 512` — it has carried a
// card title since B08, because `cardAddressSchema`'s slug is derived from one
// — and its docstring asks this file by name to **import** the constant rather
// than "restate a second 512 that can then drift from this one". So it is
// imported above.
//
// `./list` declares its own `LIST_TITLE_MAX_LENGTH = 512` and is not
// inconsistent with that: its note says there was no constant describing
// `lists.title` to import, and that `boards.title`, `lists.title` and
// `cards.title` are three independent columns three independent migrations
// could widen separately. All three are 512 **today**, which is why a reader
// scanning the three files sees one number and suspects duplication. They are
// one number by coincidence, not by construction. The rule is per column:
// import the constant that describes YOUR column if one exists, declare it if
// none does, and never share one across two columns.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// INPUT primitives — what a route ACCEPTS.
// ---------------------------------------------------------------------------

/**
 * A card title as `cards.title varchar(512)` stores it.
 *
 * `.trim().min(1)` is `boardTitleSchema`'s and `listTitleSchema`'s idiom,
 * reused rather than re-decided, and here it is `FR-BOARD-020`'s own edge case
 * in as many words — "Whitespace-only titles are rejected" — plus the rename's
 * half of the same rule: `FR-BOARD-022` AC3 says Escape leaves the title
 * unchanged, so a quick edit that commits nothing must never be able to commit
 * *emptiness* either. One primitive serves creation and rename, so the two can
 * never disagree about what "empty" means.
 *
 * ---------------------------------------------------------------------------
 * WHAT THE TRIM DOES NOT LICENSE, TWICE
 * ---------------------------------------------------------------------------
 *   - **It does not truncate.** `FR-BOARD-022`'s quick edit shows the title on
 *     the card front, which the board face elides visually; the full string is
 *     stored and returned up to the column width, and the elision is `FE-12.2`'s.
 *     Truncating here would destroy the text the editor is supposed to reopen.
 *   - **It does not normalise newlines, and it must not start.**
 *     `FR-BOARD-020`'s second edge case — "Newline characters typed (as opposed
 *     to pasted) inside the composer submit the card rather than creating a
 *     multi-line title" — is a *keystroke* rule in the composer, which is
 *     `FE-12.1`'s, and it is deliberately not enforced here: a title arriving
 *     with an interior newline is a legitimate request (it is what `FR-BOARD-021`'s
 *     paste path would produce if a line contained one), and rejecting or
 *     collapsing it server-side would make the two paths disagree about the
 *     same string. `.trim()` removes leading and trailing whitespace including
 *     newlines, which is all this boundary should claim.
 */
export const cardTitleSchema = z.string().trim().min(1).max(CARD_TITLE_MAX_LENGTH);

/**
 * One end of the gap a card is being placed into — a neighbouring card's id, or
 * `null` for an open end.
 *
 * ⚠ **`previousCardId` and `nextCardId`, never `beforeCardId`/`afterCardId`.**
 * `listNeighbourSchema` in `./list` states the argument in full and it is
 * inherited verbatim: the naming matches `rankBetween(before, after)`'s
 * argument order, and the rejected spelling is ambiguous in the worst possible
 * way — "insert *before* X" and "the card *before* X" name opposite positions,
 * both readings are natural, and a caller that picks the wrong one gets a card
 * on the wrong side of its neighbour with no error anywhere.
 *
 * `.nullable().default(null)` on both, so an omitted key and an explicit `null`
 * are the same request. What that pair of nulls *means* differs between the two
 * bodies below — and, more importantly, differs from what it means in `./list`.
 * See both.
 *
 * There is no `position` field on any body in this file, and that is SC-2
 * rather than an omission. `./rank`'s header states the rule — "no caller
 * should ever compute a rank by hand … the argument order and the null
 * convention are easy to get subtly wrong, and getting them wrong is silent" —
 * and here a client-supplied rank would make `FR-BOARD-024`'s edge case
 * unverifiable outright: "two members reordering the same list simultaneously
 * must converge on one order for both" holds only if one authority computes
 * both ranks from ranks it read in the same transaction.
 */
const cardNeighbourSchema = ulidSchema.nullable().default(null);

/**
 * `POST /cards` — `FR-BOARD-020`.
 *
 * The list is named **in the body**, not in the path, exactly as `POST /lists`
 * names its board and `POST /boards` names its workspace. That is the shipped
 * precedent for creating a child of a container, and `GET /lists/:id/cards`
 * hangs off the list prefix exactly as `GET /boards/:id/lists` hangs off the
 * board prefix. The asymmetry was settled by B08 and is not re-litigated here.
 *
 * **There is no `boardId`, and its absence is enforced rather than merely
 * omitted.** `ck_cards_parent` requires `board_id` and `list_id` to be non-null
 * together, so the card does have a board — the server reads it from the list
 * inside the transaction. Accepting one from the caller would create a body
 * that can *disagree with itself* (a list on board A, a `boardId` naming board
 * B), and there is no right answer to that request: trusting the list makes the
 * field a lie, trusting the field makes the card unreachable from its own list.
 * `strictObject` turns it into a 422 instead.
 *
 * ---------------------------------------------------------------------------
 * ⚠ BOTH NEIGHBOURS NULL MEANS "THE BOTTOM OF THE LIST" — AND THE EQUIVALENT
 * `./list` BODY MEANS "THE FAR RIGHT". SAME SHAPE, SAME SPIRIT, AND THE ONE
 * BELOW (`cardMoveBodySchema`) BREAKS WITH `./list` OUTRIGHT. READ ALL THREE.
 * ---------------------------------------------------------------------------
 * `FR-BOARD-020` has two composers and this one body serves both:
 *
 *   - AC1, the **bottom** composer: "when the user types a title and presses
 *     Enter, then the card appears as the **last** card in the list". That
 *     request carries neither neighbour, so both fields take their `null`
 *     default. This is the ordinary path and the one a client hits on almost
 *     every card it creates.
 *   - AC2, the **header** composer: "when the user creates a card, then it
 *     appears as the **first** card in the list". That request sends
 *     `nextCardId` = the current first card and no `previousCardId`.
 *
 * So both-null does **not** mean `rankBetween(null, null)`. It means *append*:
 * the server reads the last rank in the target list and ranks the new card
 * after it, which collapses to `rankBetween(null, null)` only in a list that
 * holds no cards at all. Reading the pair literally — "no neighbours, therefore
 * an empty list" — would put every new card at the **top**, which is the exact
 * opposite of AC1, and it would do so **silently**: `rankBetween(null, null)`
 * is a legal call that returns a legal rank, and it returns *the same* rank
 * every time, so two successive bottom-composer creates would also collide on
 * one value and land in `id` order rather than in typing order. `rankForNewList`
 * in `apps/server/src/routes/lists.ts` is the identical trap one level up and
 * is the code to mirror.
 *
 * An explicit `previousCardId` with a null `nextCardId` is the same append said
 * out loud, and an explicit `nextCardId` with a null `previousCardId` prepends.
 * The default is a shorthand for the first of those, not a third case.
 */
export const createCardBodySchema = z.strictObject({
  listId: ulidSchema.describe(
    'The list the card is created in. The card’s board is read from this list ' +
      'and is not accepted from the caller — see the schema note.',
  ),
  title: cardTitleSchema.describe(
    'The card title, as typed. Trimmed; a whitespace-only submission is refused ' +
      '(FR-BOARD-020 edge case). A newly created card has no other content.',
  ),
  previousCardId: cardNeighbourSchema.describe(
    'The card immediately ABOVE the gap the new card goes into. Omit both ' +
      'neighbours to create the card at the bottom of the list, which is ' +
      'FR-BOARD-020 AC1’s bottom composer; send nextCardId alone (the current ' +
      'first card) for AC2’s header composer.',
  ),
  nextCardId: cardNeighbourSchema.describe(
    'The card immediately BELOW the gap the new card goes into. See previousCardId.',
  ),
  location: cardLocationPutSchema
    .optional()
    .describe(
      'Optional initial location for cards created from Map view. It writes the ' +
        'card location columns during creation without widening the narrow Card response.',
    ),
});

export type CreateCardBody = z.infer<typeof createCardBodySchema>;

/**
 * `PATCH /cards/:id/title` — `FR-BOARD-022`. SC-4, field-scoped.
 *
 * The title moves on its own and can move nothing else — **never a
 * `PUT /cards/:id`**. `boardTitlePatchSchema` gives the argument in full and it
 * applies here at its strongest, because `cards` is the widest table in the
 * product and eight later blocks own columns on it: an absent key and a cleared
 * value are the same thing in the body a PUT receives, `FR-EXP-031`'s per-field
 * last-write-wins cannot be expressed against a whole-object write, and
 * `FR-COLLAB-026` requires that another member's edit to a different field must
 * not clobber the viewer's draft. A `PUT` here would let a quick edit from the
 * board face — which knows *only* the title — blank a description, a due date
 * and a cover it never loaded.
 *
 * The position is a separate route below for the same reason and not merely by
 * symmetry: a rename and a drag are different requirements with different
 * bodies, and the drag's body cannot contain a value the caller may choose.
 *
 * `FR-BOARD-024` states the other half explicitly — "Ordering is unaffected by
 * edits to card content", AC2 — so this body's inability to carry a position is
 * a requirement rather than a convention.
 */
export const cardTitlePatchSchema = z.strictObject({
  title: cardTitleSchema.describe(
    'The new card title. Trimmed; an empty or whitespace-only submission is ' +
      'refused and the previous title stands (FR-BOARD-022 AC3).',
  ),
});

export type CardTitlePatch = z.infer<typeof cardTitlePatchSchema>;

/**
 * `POST /cards/:id/move` — `FR-BOARD-025`, "Dropping commits the move for all
 * members and records it in board activity". SC-4, and SC-2's fractional rank
 * doing the work.
 *
 * ---------------------------------------------------------------------------
 * ONE ROUTE, TWO GESTURES — WHICH IS WHY `listId` IS REQUIRED
 * ---------------------------------------------------------------------------
 * `FR-BOARD-025` describes one gesture with two outcomes: dragging "within a
 * list, between lists on the same board". There is deliberately **no separate
 * `PATCH /cards/:id/position`**. A within-list reorder sends the card's
 * *current* list; a between-list move sends another list on the same board.
 * The caller does not have to know which one it is doing, and — this is the
 * point — neither does the client that computed the drop target from a DOM
 * position, because a drag that starts in list A and ends in list A is the same
 * drop as one that ends in list B.
 *
 * `listId` is therefore **required**, with no default and no nullable. A body
 * that omitted it would mean "reorder within whatever list this card is in",
 * which is a second, implicit gesture the route would have to branch on, and it
 * would make the between-list move the only one that names its destination —
 * the exact shape that grows into two routes later.
 *
 * **A `listId` naming a list on ANOTHER BOARD is refused, and by the route
 * rather than by this schema.** `FR-BOARD-025`'s own sentence closes it:
 * dragging works "within a list, between lists on the same board — and *not*
 * between boards (see `FR-BOARD-026`)". A Zod schema cannot express "on the
 * same board as the card"; what it can do is refuse to carry a `boardId`, so
 * this body cannot even *ask* for a cross-board move. The route answers the
 * same `404` it answers for a list that does not exist — a caller must not
 * learn that a list exists on a board they were not addressing.
 *
 * ---------------------------------------------------------------------------
 * ⚠ BOTH NEIGHBOURS NULL IS **LEGAL** HERE. `listPositionPatchSchema` IN
 * `./list` REFUSES IT WITH A 422. THAT DIVERGENCE IS DELIBERATE, IT IS THE
 * MOST "HARMONISABLE"-LOOKING THING IN EITHER FILE, AND HARMONISING IT BREAKS
 * A REQUIREMENT WHICHEVER DIRECTION IT IS DONE IN.
 * ---------------------------------------------------------------------------
 * `./list` refused the null pair because a list patch carries no destination of
 * its own: `{ previousListId: null, nextListId: null }` says "move this list to
 * nowhere", which is not a request anybody can have meant, and the alternative
 * readings ("leave it where it is", "send it to one end") are a silent no-op
 * and a guess.
 *
 * Here the destination is carried by `listId`, so the same pair of nulls says
 * something complete and ordinary: **"the bottom of that list"**. Two of
 * `FR-BOARD-025`'s everyday paths produce exactly it —
 *
 *   - dropping a card into an **empty** list, which has no neighbours to name;
 *   - dropping a card **below every card** in a populated list.
 *
 * Refusing the pair here would make the first one unexpressible and force the
 * client to special-case the second. Requiring a neighbour "for symmetry with
 * `./list`" is therefore not a tidy-up; it is the removal of AC1's own worked
 * example ("the user drags it into list B") for the case where list B is empty.
 *
 * The reading is the same one `createCardBodySchema` takes, which is what makes
 * it defensible rather than convenient: in this file, *both neighbours null
 * means the bottom of the named list*, on both bodies, always.
 *
 * ---------------------------------------------------------------------------
 * THE NO-OP THE SCHEMA CANNOT SEE, RECORDED HERE BECAUSE THIS IS WHERE A
 * READER LOOKS FIRST
 * ---------------------------------------------------------------------------
 * `FR-BOARD-025`'s edge case: "Dropping a card back on its original position
 * must be a no-op and must not create an activity entry." That is a comparison
 * between the card's *current* rank and the gap the request names, so only the
 * handler can make it — it needs the row. A naive handler mints a new rank
 * strictly between the same two neighbours and emits `card.position_changed`,
 * which fails the edge case with a `200` and a perfectly plausible rank, and no
 * test that only reads the response can tell. The route detects it **before**
 * writing, using `compareRanks` from `./rank`, and writes and emits nothing.
 */
export const cardMoveBodySchema = z.strictObject({
  listId: ulidSchema.describe(
    'The list the card is dropped into — the card’s CURRENT list for a ' +
      'within-list reorder, another list ON THE SAME BOARD for a move between ' +
      'lists. Required in both cases: one gesture, one route. A list on another ' +
      'board is refused (FR-BOARD-025; moving a card between boards is ' +
      'FR-BOARD-026).',
  ),
  previousCardId: cardNeighbourSchema.describe(
    'The card immediately ABOVE the gap this card is dropped into, in the ' +
      'target list. Send this alone to drop the card at the bottom of that list.',
  ),
  nextCardId: cardNeighbourSchema.describe(
    'The card immediately BELOW the gap this card is dropped into, in the ' +
      'target list. Send this alone to drop the card at the top of that list. ' +
      'Omitting BOTH neighbours is legal and means the bottom of the target ' +
      'list — that is how a card is dropped into an empty list.',
  ),
});

export type CardMoveBody = z.infer<typeof cardMoveBodySchema>;

// ---------------------------------------------------------------------------
// Views — what a route RETURNS.
//
// Width-bounded strings, no format checks, no transforms — see the note above
// the input primitives, and `./user`'s longer version of the same argument.
// ---------------------------------------------------------------------------

/**
 * A card as every B12 route that answers with a body returns it — `POST
 * /cards`, `GET /lists/:id/cards`, `PATCH /cards/:id/title` and
 * `POST /cards/:id/move`.
 *
 * One view for all four, rather than a narrower shape per route, for the reason
 * `listSchema` gives: they all answer the same question — *what is this card
 * now* — and a client that has just dragged a card needs the same fields as one
 * that has just created it. A `CardSummary` for the board face and a `Card` for
 * the mutations would be two shapes kept identical by hand.
 *
 * ⚠ **It is not `cardAddressSchema` and it does not replace it.** That shape
 * (in `./board`, `GET /cards/by-short-link/:shortLink`) answers "where is this
 * card and what board is behind it", nests the whole board because
 * `FR-EXP-015` AC2 needs it, and carries a `slug`. This one answers "what is
 * this card on the board face", carries `boardId` as an id, and carries no
 * `slug` — see `path` below. Neither is the other's superset and merging them
 * would give one of the two routes fields it has no requirement for.
 *
 * ---------------------------------------------------------------------------
 * WHY THE LIFECYCLE IS ONE BOOLEAN
 * ---------------------------------------------------------------------------
 * `cards` carries `archived_at` and `deleted_at`, both nullable `timestamptz`,
 * and neither reaches the wire. `listSchema`'s and `boardSchema`'s lifecycle
 * notes are the long form and they transfer intact: `archived` is the whole of
 * what a caller can act on, and `deletedAt` could only ever be `null` because a
 * deleted card is absent from every read path and no route writes it back.
 *
 * ⚠ **`archived` is the CARD's own flag and says nothing about its list.** This
 * is B11's forward bill arriving, and it is the one silent failure in this
 * block. `FR-BOARD-015` requires restore to tell cards archived *with* a list
 * from cards archived *individually beforehand*, and the only thing that
 * distinguishes them is that the second group has its own `cards.archived_at`
 * and the first does not — so `POST /lists/:id/archive` writes one column on
 * one row and never touches `cards`. The consequence for every card read:
 * **filter on the parent list's `archived_at IS NULL` as well as the card's
 * own**, or an archived list's cards keep appearing on the board face with
 * `archived: false`, which is *true of the card* and wrong for the board.
 */
export const cardSchema = z.object({
  id: ulidSchema,
  /**
   * `cards.board_id char(26)` — nullable in the column, **never null here**.
   *
   * `ck_cards_parent` forces `board_id` and `list_id` to be non-null together
   * (or both null with an `inbox_owner_id`), so a card on a board always has
   * one. The nullable case is the personal inbox, which this view deliberately
   * cannot express — see the module header.
   *
   * On the wire because every permission guard in this product takes a board
   * id, because it is the room a live update arrives on (`FR-BOARD-025` AC4),
   * and because a card moved between lists stays on the same board — which is
   * a fact a client can only check if it is told. **B12 never changes it.**
   * `FR-BOARD-026` is the cross-board move and it is not this block's.
   */
  boardId: ulidSchema,
  /**
   * `cards.list_id char(26)` — nullable in the column, never null here, for the
   * same `ck_cards_parent` reason as `boardId`.
   *
   * This is the field `POST /cards/:id/move` changes on a between-list move,
   * and the field it leaves alone on a within-list reorder. A client comparing
   * it against the list it dragged from is how the two gestures are told apart
   * after the fact.
   */
  listId: ulidSchema,
  title: z.string().max(CARD_TITLE_MAX_LENGTH),
  /**
   * `cards.position varchar(64) COLLATE "C"` — the fractional rank the list is
   * ordered by, `RANK_MAX_LENGTH` from `./rank`.
   *
   * **Opaque**, exactly as `listSchema.position` is. It is on the wire because
   * a client rendering a list needs the key it was ordered by — to hold a
   * stable order across an optimistic drag, and to tell a locally reordered
   * card from a reordered one that arrived over the socket. It is emphatically
   * **not** on the wire so a client can compute one: `cardMoveBodySchema` takes
   * neighbours and no client may send a rank (SC-2).
   *
   * Compare it with `compareRanks` from `./rank` and nothing else.
   * `localeCompare` is not byte order and would sort a list differently from
   * the database that produced it — a divergence with no error attached, which
   * is exactly the failure `FR-BOARD-024` ("no implicit sort") exists to
   * prevent.
   */
  position: z.string().max(RANK_MAX_LENGTH),
  /**
   * `cards.short_link varchar(16) COLLATE "C"`, `NOT NULL UNIQUE` — the
   * permanent half of the card's address (`FR-EXP-015`, `FR-CARD-005`).
   *
   * Bounded by the column width and **not** parsed through `shortLinkSchema`:
   * that is the *input* primitive, with the base-62 regex a `:shortLink` path
   * param is checked against, and reusing it on a view would make this schema
   * refuse a row the database legitimately holds. The same input/view split
   * `boardSchema.shortLink` draws.
   *
   * B12 is the first block that can put a value in this column, and therefore
   * the first block that gives `GET /cards/by-short-link/:shortLink` — shipped
   * by B08 — a row to resolve.
   */
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  /**
   * `cards.card_number integer` — board-scoped, sequential, **never reused**
   * (`FR-CARD-005`: "deleting a card does not free its number").
   *
   * `cardNumberSchema` from `./board`, imported rather than restated so the
   * allocator and this view cannot disagree about what a legal number is.
   * Nullable in the column for the inbox card; non-null here for the
   * `ck_cards_parent` reason `boardId` states.
   */
  cardNumber: cardNumberSchema,
  /** `KEY-NNNN`, or `null` exactly when `path` carries the short link — `cardReferenceSchema` in `./board`. */
  ref: cardReferenceSchema.nullable(),
  /**
   * `/c/<KEY-NNNN>/<slug>` for a caller who may use the board's card
   * references, `/c/<shortLink>/<slug>` for everybody else — `cardPath()` in
   * `./address`, computed at read time from the title and per viewer
   * (`docs/notes/card-keys.md`). `FR-CARD-005`, `FR-EXP-015`.
   *
   * **The assembled path only, and no bare `slug`** — which is the one place
   * this view diverges from `boardSchema`, and it is a decision rather than an
   * omission. `boardSchema` carries `slug` *and* `boardAddressSchema` adds
   * `path`, because `FR-EXP-015` AC1 has a client correcting its address bar
   * after a rename and the resolver has to state what the current slug is.
   * Nothing in `FR-BOARD-020`/`022`/`024`/`025` asks a board-face client to
   * re-derive an address: it renders a link and follows it. `cardAddressSchema`
   * still carries the bare slug for the resolver that needs it.
   *
   * Served at all — rather than left to the client to build — for the reason
   * `./address` states: **one rule, one implementation.** A second builder
   * drifts on the first rename, and the drift is invisible because the segment
   * is never parsed back.
   */
  path: z.string().min(1),
  /**
   * `cards.archived_at IS NOT NULL` — `FR-BOARD-028`. See the lifecycle note
   * above, including why this says nothing about the card's list.
   *
   * ⚠ **It is `false` on every card B12 can produce**, because no route in this
   * block archives one: `FR-BOARD-028` is `BE-22.1`'s and the archived-items
   * surface is `BE-22.2`'s (`FR-EXP-010`). It is on the wire regardless because
   * `GET /lists/:id/cards` filters on it and a client must be able to render
   * what it was filtered by — and because the integration suite can seed an
   * archived card directly, which is how `cardCount`'s "open and archived
   * alike" clause (`FR-BOARD-016`) is testable at all before `BE-22.1` lands.
   */
  archived: z.boolean(),
  /**
   * `cards.created_at`, serialised as the driver returns it — a bounded string
   * rather than `z.iso.datetime()` or a coerced `Date`, for the reason
   * `workspaceMemberSchema.joinedAt` states in full: the column is
   * `timestamptz` and `pg` hands it back in Postgres' own spelling
   * (`2026-08-10 12:00:00.123+00`), a legitimate instant and not an ISO 8601
   * `T`/`Z` form, so pinning the serialisation here would make this schema
   * reject the value the database actually produced.
   *
   * Carried for the reason `listSchema` records against itself and no better
   * one: the block contract fixes this view's field set. No acceptance
   * criterion in `FR-BOARD-020`/`022`/`024`/`025` names a card's creation time
   * — `FR-BOARD-013`'s "date created" sort is `FR-BOARD-013`, V1, and not this
   * block. **A field is added here when a route needs it**; these two
   * timestamps are the exception rather than the pattern.
   */
  createdAt: z.string().min(1),
  /**
   * `cards.updated_at`, same serialisation note as `createdAt`.
   *
   * Written by `trg_cards_set_updated_at`, never by a route. A second writer of
   * a trigger-derived column is the mistake `users.email_verified_at` exists to
   * avoid.
   *
   * ⚠ It is also what makes `FR-BOARD-025`'s drop-in-place edge case observable
   * from outside: a no-op move must return the card **byte-identical**, this
   * field included. A handler that "harmlessly" re-wrote `position` to the same
   * value would still bump the trigger, and the only visible symptom would be
   * this string changing.
   */
  updatedAt: z.string().min(1),
});

export type Card = z.infer<typeof cardSchema>;

/**
 * `GET /lists/:id/cards` — a list's cards in list order.
 *
 * Ordered by the server, `ORDER BY position, id` (`./rank`'s rule 2 — the index
 * on `position` is deliberately non-unique, so `position` alone lets the
 * planner return ties in any order, and a drag race is exactly how a tie
 * appears). A client renders the array as received and does not re-sort it,
 * which is `FR-BOARD-024`'s "no implicit sort" on the client side of the wire.
 *
 * ⚠ **There is no `?state` query parameter, unlike `GET /boards/:id/lists`, and
 * that asymmetry is a decision.** `listStateQuerySchema` exists because
 * `FR-EXP-010` gives archived lists a surface to be listed on and B11 shipped
 * the routes that put them there. Nothing in B12 can archive a card —
 * `FR-BOARD-028` is `BE-22.1`'s — so a `?state=archived` here would be a filter
 * over a population no route in the product can create, and it would answer
 * `[]` to a caller who has no way to tell "none archived" from "not
 * implemented". The archived-items surface as a whole is `BE-22.2`
 * (`FR-EXP-010`), which is also where `ORDER BY archived_at DESC` belongs —
 * this route's order is positional and always will be. So this endpoint serves
 * exactly the board face: live, unarchived cards in an unarchived list.
 */
export const cardListSchema = z.array(cardSchema);
